import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useHasFF, userActivity } from 'venn-utils';
import { compareColumnOpen } from 'venn-state';
import { Constants } from '../Layout';
import { Icon } from 'venn-ui-kit';
import { isNil } from 'lodash';

interface ResizableAllocationPanelWrapperProps {
  children: React.ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
}

const DEFAULT_MIN_WIDTH = 300;
const DEFAULT_MAX_WIDTH = 600;

const ResizableContent = React.memo<{ children: React.ReactNode }>(({ children }) => (
  <div className="h-full overflow-hidden">{children}</div>
));

ResizableContent.displayName = 'ResizableContent';

/**
 * Wrapper component that makes the allocation panel resizable horizontally.
 * Uses Pointer Events.
 * Calculates the default width based on historicals and compare mode feature flags.
 * @param defaultWidth - Optional override for the default width. If not provided, calculates based on feature flags.
 * @param minWidth - The minimum width allowed (defaults to calculated defaultWidth or DEFAULT_MIN_WIDTH)
 * @param maxWidth - The maximum width allowed (defaults to DEFAULT_MAX_WIDTH)
 * @param onWidthChange - Optional callback when width changes
 */
export const ResizableAllocationPanelWrapper: React.FC<ResizableAllocationPanelWrapperProps> = ({
  children,
  defaultWidth: defaultWidthProp,
  minWidth,
  maxWidth = DEFAULT_MAX_WIDTH,
  onWidthChange,
}) => {
  const hasHistoricals = useHasFF('historical_portfolios_ff');
  const STORAGE_KEY = 'allocator-panel-width';
  const isCompareOpen = useRecoilValue(compareColumnOpen);

  const calculatedDefaultWidth = useMemo(
    () =>
      isCompareOpen
        ? hasHistoricals
          ? Constants.ALLOCATION_PANEL_TOTAL_WIDTH_WITH_HISTORICALS
          : Constants.ALLOCATION_PANEL_TOTAL_WIDTH
        : hasHistoricals
          ? Constants.ALLOCATION_PANEL_MAIN_WIDTH_WITH_HISTORICALS
          : Constants.ALLOCATION_PANEL_MAIN_WIDTH,
    [isCompareOpen, hasHistoricals],
  );

  const defaultWidth = defaultWidthProp ?? calculatedDefaultWidth;

  const dynamicMinWidth = useMemo(
    () =>
      isCompareOpen
        ? hasHistoricals
          ? Constants.ALLOCATION_PANEL_TOTAL_WIDTH_WITH_HISTORICALS
          : Constants.ALLOCATION_PANEL_TOTAL_WIDTH
        : hasHistoricals
          ? Constants.ALLOCATION_PANEL_MAIN_WIDTH_WITH_HISTORICALS
          : Constants.ALLOCATION_PANEL_MAIN_WIDTH,
    [isCompareOpen, hasHistoricals],
  );

  const effectiveMinWidth = minWidth ?? Math.max(dynamicMinWidth, DEFAULT_MIN_WIDTH);
  const [width, setWidth] = useState(() => {
    const storedWidth = userActivity.getLocalStorageItemPerUser(STORAGE_KEY);
    const initialWidth = storedWidth ? JSON.parse(storedWidth) : defaultWidth;
    // Always ensure the initial width respects the minimum width, even if stored value is smaller
    return Math.max(initialWidth, effectiveMinWidth);
  });
  const [hasManuallyResized, setHasManuallyResized] = useState(() => {
    const storedWidth = userActivity.getLocalStorageItemPerUser(STORAGE_KEY);
    return !isNil(storedWidth);
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasManuallyResized) {
      // If user hasn't manually resized, always use the calculated default (clamped to minimum)
      const clampedWidth = Math.max(calculatedDefaultWidth, effectiveMinWidth);
      if (width !== clampedWidth) {
        setWidth(clampedWidth);
      }
    } else if (width < effectiveMinWidth) {
      // If user has manually resized but current width is less than the effective minimum, expand to minimum
      setWidth(effectiveMinWidth);
    }
  }, [calculatedDefaultWidth, hasManuallyResized, width, effectiveMinWidth]);

  useEffect(() => {
    onWidthChange?.(width);
  }, [width, onWidthChange]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const resizer = e.currentTarget;
    resizer.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const resizer = e.currentTarget;
    if (!resizer.hasPointerCapture(e.pointerId)) return;

    const container = containerRef.current;
    if (!container) return;

    const currentWidth = container.getBoundingClientRect().width;
    const newWidth = Math.min(maxWidth, Math.max(effectiveMinWidth, currentWidth + e.movementX));

    setWidth(newWidth);
    setHasManuallyResized(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const resizer = e.currentTarget;
    resizer.releasePointerCapture(e.pointerId);
    userActivity.setLocalStorageItemPerUser(STORAGE_KEY, JSON.stringify(width));
  };

  return (
    <div ref={containerRef} style={{ width: `${width}px` }} className="relative h-full overflow-hidden">
      <ResizableContent>{children}</ResizableContent>
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        title="Drag to resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize allocator panel"
        aria-valuemin={effectiveMinWidth}
        aria-valuemax={maxWidth}
        aria-valuenow={Math.round(width)}
        className="absolute right-0 top-0 h-full cursor-col-resize touch-none flex items-center justify-center w-fit bg-venn-grey-30"
      >
        <Icon type="grip-vertical" className="text-[10px] px-[3px] text-venn-grey-60" aria-hidden />
      </div>
    </div>
  );
};
